import { useState } from "react";
import type { Budget, GrowthPlan, GrowView } from "../../api/growth";
import { Banner } from "../../components/Banner";
import { Field, inputShell } from "../../components/Field";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { money, numberOrNull } from "../../lib/format";

interface GrowScreenProps {
  view: GrowView;
  busy: boolean;
  onChange: (budget: Budget, plan: GrowthPlan) => void;
  onComplete: () => void;
}

const ACTION_HINTS = [
  "e.g. Pitch 3 retainer clients",
  "e.g. Raise my Standard package to $250",
  "e.g. Post one case study every two weeks",
];

/** Step 6: a budget built on the lowest month, a safety buffer, and a 90-day plan. */
export function GrowScreen({ view, busy, onChange, onComplete }: GrowScreenProps) {
  const [budget, setBudget] = useState<Budget>(view.step.budget);
  const [plan, setPlan] = useState<GrowthPlan>(view.step.plan);
  const [budgetGate, planGate] = view.gate.items;
  const calc = view.calc;

  const setB = (patch: Partial<Budget>) => {
    const next = { ...budget, ...patch };
    setBudget(next);
    onChange(next, plan);
  };
  const setP = (patch: Partial<GrowthPlan>) => {
    const next = { ...plan, ...patch };
    setPlan(next);
    onChange(budget, next);
  };
  const num = (key: keyof Budget, label: string, placeholder = "") => (
    <Field label={label} htmlFor={`b-${key}`}>
      <input
        id={`b-${key}`}
        type="number"
        min={0}
        className={inputShell}
        placeholder={placeholder}
        value={(budget[key] as number | null | undefined) ?? ""}
        onChange={(e) => setB({ [key]: numberOrNull(e.target.value) })}
      />
    </Field>
  );

  return (
    <div>
      <ScreenHeader
        kicker="Step 6 · Keep It and Grow · while earning"
        title="Hold on to the money, and plan the next level."
        sub="Freelance income goes up and down. This step protects the wins you've made: a budget built on your lowest month, a safety buffer, and a 90-day plan."
      />
      <Guide guideKey="gig6" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />
      <Section
        title="1. Your budget for irregular income"
        hint="Enter what you earned from gigs in each of the last 3 months (use 0 for a month with nothing)."
        status={<Pill ok={Boolean(budgetGate?.done)} />}
      >
        <div className="grid grid-cols-3 gap-x-3">
          {num("m1", "Month 1 (USD)")}
          {num("m2", "Month 2 (USD)")}
          {num("m3", "Month 3 (USD)")}
        </div>
        <div className="max-w-[320px]">
          {num("ess", "Your essential costs per month (USD)", "rent, food, transport, data")}
        </div>
        <p className="text-[13px] text-text-muted">
          Split your lowest month like this (must add up to 100%):
        </p>
        <div className="grid grid-cols-3 gap-x-3">
          {num("needs", "Needs %")}
          {num("wants", "Wants %")}
          {num("save", "Savings %")}
        </div>
        {view.warnings.map((warning) => (
          <Banner key={warning} tone={warning.includes("100%") ? "bad" : "warn"}>
            {warning}
          </Banner>
        ))}
        {calc ? (
          <div className="rounded-[var(--radius-control)] border border-border p-4">
            <table className="w-full text-[14px]">
              <tbody>
                {(
                  [
                    ["Plan on your lowest month", calc.low, true],
                    ["Your average month", calc.avg, false],
                    [`Needs (${budget.needs ?? 0}%)`, calc.needsAmt, false],
                    [`Wants (${budget.wants ?? 0}%)`, calc.wantsAmt, false],
                    [`Savings (${budget.save ?? 0}%)`, calc.saveAmt, false],
                    ["Safety buffer target (3 months of essentials)", calc.buffer, true],
                  ] as [string, number, boolean][]
                ).map(([label, value, bold]) => (
                  <tr key={label}>
                    <td className="py-1">{label}</td>
                    <td className={`py-1 text-right tabular ${bold ? "font-bold" : ""}`}>
                      {money(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[13px] text-text-muted">
              {calc.months
                ? `At this rate you reach your buffer in about ${calc.months} month${calc.months > 1 ? "s" : ""}. In a month that beats your lowest, save the extra.`
                : "Set a savings % above 0 to start your buffer."}
            </p>
          </div>
        ) : null}
        <div className="mt-3 max-w-[320px]">
          <Field label="My weekly 15-minute money review day" htmlFor="b-day">
            <select
              id="b-day"
              className={inputShell}
              value={budget.reviewDay ?? ""}
              onChange={(e) => setB({ reviewDay: e.target.value || null })}
            >
              <option value="">Choose...</option>
              {view.reviewDays.map((day) => (
                <option key={day}>{day}</option>
              ))}
            </select>
          </Field>
        </div>
      </Section>
      <Section
        title="2. Your mini financial plan and 90-day growth plan"
        status={<Pill ok={Boolean(planGate?.done)} />}
      >
        <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
          <Field label="Savings goal (USD)" htmlFor="p-goal">
            <input
              id="p-goal"
              type="number"
              min={0}
              className={inputShell}
              value={plan.goal ?? ""}
              onChange={(e) => setP({ goal: numberOrNull(e.target.value) })}
            />
          </Field>
          <Field label="Reach it by" htmlFor="p-by">
            <input
              id="p-by"
              type="month"
              className={inputShell}
              value={plan.goalBy ?? ""}
              onChange={(e) => setP({ goalBy: e.target.value || null })}
            />
          </Field>
          <Field label="How I'll track my money" htmlFor="p-tool">
            <select
              id="p-tool"
              className={inputShell}
              value={plan.tool ?? ""}
              onChange={(e) => setP({ tool: e.target.value || null })}
            >
              <option value="">Choose...</option>
              {view.trackingTools.map((tool) => (
                <option key={tool}>{tool}</option>
              ))}
            </select>
          </Field>
          <Field label="Monthly earning target in 90 days (USD)" htmlFor="p-target">
            <input
              id="p-target"
              type="number"
              min={0}
              className={inputShell}
              value={plan.target ?? ""}
              onChange={(e) => setP({ target: numberOrNull(e.target.value) })}
            />
          </Field>
        </div>
        <div className="mb-1.5 text-[12.5px] font-bold">Three actions that will get me there</div>
        {(["a1", "a2", "a3"] as const).map((key, index) => (
          <input
            key={key}
            aria-label={`Action ${index + 1}`}
            className={`${inputShell} mb-2`}
            placeholder={ACTION_HINTS[index]}
            value={plan[key] ?? ""}
            onChange={(e) => setP({ [key]: e.target.value })}
          />
        ))}
        <Field label="One skill I'll learn next" htmlFor="p-skill">
          <input
            id="p-skill"
            className={inputShell}
            placeholder="e.g. Motion graphics in After Effects"
            value={plan.skill ?? ""}
            onChange={(e) => setP({ skill: e.target.value })}
          />
        </Field>
      </Section>
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete the pathway"
        next={{ to: "/grow-library", label: "Open the Grow Library" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}
